import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { createLogger } from '@/utils/logger';
import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TextInput } from 'react-native';

const logger = createLogger('VERIFY_OTP');

const DIGITS = 6;
const TARGET_GAP = 12;
const MIN_GAP = 4;
const MIN_WIDTH = 22;
const MAX_WIDTH = 48;
const MIN_HEIGHT = 40;
const MAX_HEIGHT = 56;
const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 22;

export function useOtpVerification() {
  const { sendVerificationOTP, verifyEmailOTP, user } = useAuth();
  const { email } = useLocalSearchParams<{ email: string }>();

  const userEmail = email || user?.email || '';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');
  const [otpRowWidth, setOtpRowWidth] = useState<number | null>(null);

  const inputRefs = useRef<(TextInput | null)[]>([]);
  const hasCheckedEmailRef = useRef(false);

  useEffect(() => {
    if (!hasCheckedEmailRef.current && !userEmail) {
      hasCheckedEmailRef.current = true;
      logger.log('No email available on mount, redirecting to login');
      toast.error('No email address found. Please log in again.');
      router.replace({ pathname: '/(auth)/login' });
    } else if (!hasCheckedEmailRef.current && userEmail) {
      hasCheckedEmailRef.current = true;
    }
  }, [userEmail]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    const timer = setTimeout(() => inputRefs.current[0]?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleOTPChange = useCallback((value: string, index: number) => {
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 1) {
      const digit = digits;
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);
      setError('');

      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      if (digit && index === 5 && newOtp.every(d => d !== '')) {
        handleVerifyOTP(newOtp.join(''));
      }
      return;
    }

    const newOtp = [...otp];
    let lastFilledIndex = index;
    for (let i = 0; i < digits.length && index + i < 6; i++) {
      newOtp[index + i] = digits[i];
      lastFilledIndex = index + i;
    }
    setOtp(newOtp);
    setError('');

    const nextEmptyIndex = lastFilledIndex + 1;
    if (nextEmptyIndex < 6 && !newOtp[nextEmptyIndex]) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else if (newOtp.every(d => d !== '')) {
      handleVerifyOTP(newOtp.join(''));
    }
  }, [otp]);

  const handleKeyPress = useCallback((event: any, index: number) => {
    if (event.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [otp]);

  const handleVerifyOTP = useCallback(async (otpCode?: string) => {
    if (!userEmail) {
      setError('Email is required for verification');
      return;
    }

    const otpToVerify = otpCode || otp.join('');
    if (otpToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await verifyEmailOTP(userEmail, otpToVerify);
      if (result.error) {
        setError(result.error.message || 'Invalid or expired code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        logger.log('Email verification successful, redirecting to login');
        router.replace({ pathname: '/(auth)/login' });
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }, [userEmail, otp, verifyEmailOTP]);

  const handleResendOTP = useCallback(async () => {
    if (!userEmail || resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setError('');

    try {
      const result = await sendVerificationOTP(userEmail);
      if (result.error) {
        setError(result.error.message || 'Failed to resend code. Please try again.');
      } else {
        setResendCooldown(60);
        toast.success('Verification code sent');
      }
    } catch {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }, [userEmail, resendCooldown, isResending, sendVerificationOTP]);

  const computeBoxSizes = useCallback(() => {
    if (!otpRowWidth) {
      return { boxWidth: MAX_WIDTH, boxHeight: MAX_HEIGHT, fontSize: MAX_FONT_SIZE, gap: TARGET_GAP };
    }

    let gap = TARGET_GAP;
    let boxWidth = (otpRowWidth - (DIGITS - 1) * gap) / DIGITS;

    if (boxWidth < MIN_WIDTH) {
      boxWidth = MIN_WIDTH;
      gap = (otpRowWidth - DIGITS * boxWidth) / (DIGITS - 1);
      gap = Math.max(MIN_GAP, Math.min(TARGET_GAP, gap));
      boxWidth = (otpRowWidth - (DIGITS - 1) * gap) / DIGITS;
    }

    boxWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, boxWidth));
    const boxHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.round(boxWidth * 1.15)));
    const fontSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, Math.round(boxWidth * 0.45)));

    return { boxWidth, boxHeight, fontSize, gap };
  }, [otpRowWidth]);

  const handleOtpRowLayout = useCallback((event: any) => {
    const { width } = event.nativeEvent.layout;
    if (width > 0 && width !== otpRowWidth) {
      setOtpRowWidth(width);
    }
  }, [otpRowWidth]);

  const navigateToLogin = useCallback(() => {
    router.replace({ pathname: '/(auth)/login' });
  }, []);

  return {
    otp,
    isLoading,
    isResending,
    resendCooldown,
    error,
    userEmail,
    inputRefs,
    handleOTPChange,
    handleKeyPress,
    handleVerifyOTP,
    handleResendOTP,
    computeBoxSizes,
    handleOtpRowLayout,
    navigateToLogin,
  };
}
